// Footer.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaFacebookF,
  // FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";
import logo from "../assets/white-1.svg";

const Footer = ({ setSelectedService }) => {
  const navigate = useNavigate();

  // Service data matching your Services.jsx
  const serviceDetails = {
    "Mobile Development": {
      id: "mobile-development",
      // ... include the full service object from Services.jsx
    },
    "AI & Machine Learning": {
      id: "ai-ml",
    },
    "Web Development": {
      id: "software-development",
    },
    "Desktop Application": {
      id: "erp",
    },
    "UI/UX Design": {
      id: "ui-ux-design",
    },
    "Digital Marketing": {
      id: "digital-marketing",
    },
    "Contract Staffing": {
      id: "staffing",
    },
  };

  const services = [
    { name: "Mobile Development", path: "/services/mobile-development" },
    { name: "AI & Machine Learning", path: "/services/ai-ml" },
    { name: "Web Development", path: "/services/software-development" },
    { name: "Desktop Application", path: "/services/erp" },
    { name: "UI/UX Design", path: "/services/ui-ux-design" },
    { name: "Digital Marketing", path: "/services/digital-marketing" },
    { name: "Contract Staffing", path: "/services/staffing" },
  ];

  const company = [
    { name: "About", href: "/about" },
    { name: "Services", href: "/services" },
    { name: "Careers", href: "/careers" },
    { name: "Contact", href: "/contact" },
  ];

  const socialLinks = [
    {
      icon: FaFacebookF,
      href: "https://www.facebook.com/profile.php?id=61558583624736",
      label: "Facebook",
    },
    // {
    //   icon: FaTwitter,
    //   href: "#",
    //   label: "Twitter",
    // },
    {
      icon: FaInstagram,
      href: "https://www.instagram.com/nexonetics/",
      label: "Instagram",
    },
    {
      icon: FaLinkedinIn,
      href: "https://www.linkedin.com/company/100580437/",
      label: "LinkedIn",
    },
  ];

  // Handle service click - navigate to services page and scroll to that service
  const handleServiceClick = (serviceId) => {
    navigate("/services");
    // Scroll to the service section after a short delay
    setTimeout(() => {
      const element = document.getElementById(serviceId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  return (
    <footer className="relative bg-black text-white overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></div>

      <div className="px-6 md:px-16 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Column 1: Logo & Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/">
              <img
                src={logo}
                alt="Nexonetics"
                className="w-48 mb-6 cursor-pointer hover:opacity-80 transition-opacity"
              />
            </Link>
            <p className="text-gray-400 mb-6 text-sm leading-relaxed">
              Transforming your visionary ideas into powerful, tangible
              solutions through innovative software development.
            </p>

            {/* Social Media */}
            <div className="flex gap-3 mt-8">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 border border-white/10 text-gray-400 hover:bg-cyan-500 hover:text-white hover:border-cyan-500 transition-all"
                >
                  <social.icon className="text-sm" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Column 2: Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-lg font-bold mb-6 text-white">Services</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link to={service.path}>
                    <motion.div
                      whileHover={{ x: 5 }}
                      className="text-gray-400 hover:text-cyan-400 transition-colors text-sm flex items-center gap-2 group cursor-pointer"
                    >
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                        →
                      </span>
                      {service.name}
                    </motion.div>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Column 3: Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-lg font-bold mb-6 text-white">Company</h3>
            <ul className="space-y-3 mb-8">
              {company.map((item, index) => (
                <li key={index}>
                  <Link to={item.href}>
                    <motion.div
                      whileHover={{ x: 5 }}
                      className="text-gray-400 hover:text-cyan-400 transition-colors text-sm flex items-center gap-2 group cursor-pointer"
                    >
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                        →
                      </span>
                      {item.name}
                    </motion.div>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Column 4: Contact Us */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-lg font-bold mb-6 text-white">Contact Us</h3>
            <div className="space-y-4">
              <motion.a
                href="tel:+919645509057"
                whileHover={{ x: 5 }}
                className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <FaPhone className="text-cyan-400 mt-1 text-sm" />
                <div>
                  <p className="text-sm">+91 9645509057</p>
                  <p className="text-sm">+1 (315) 657-1957</p>
                  <p className="text-sm">1-888-584-8596</p>
                </div>
              </motion.a>

              <motion.a
                href="mailto:info@nexonetics.com"
                whileHover={{ x: 5 }}
                className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <FaEnvelope className="text-cyan-400 mt-1 text-sm" />
                <p className="text-sm">info@nexonetics.com</p>
              </motion.a>

              <motion.a
                href="https://www.google.com/maps/search/?api=1&query=Ponoth+Ln+Kaloor+Ernakulam+Kerala+682017"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: 5 }}
                className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <FaMapMarkerAlt className="text-cyan-400 mt-1 text-sm flex-shrink-0" />
                <p className="text-sm leading-relaxed">
                  Ponoth Ln, Deshabhimani Junction,
                  <br />
                  Kaloor, Ernakulam,
                  <br />
                  Kerala 682017
                </p>
              </motion.a>
            </div>
          </motion.div>
        </div>

  
      </div>

      {/* Background Decorative Elements */}
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-[100px] -z-10"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-[100px] -z-10"></div>
    </footer>
  );
};

export default Footer;
